import React, { useEffect, useState, useMemo, memo } from "react";
import {
  ProductCardProductsType,
  ProductCardIsinToNameType,
  ProductCardMisureDiRischioType,
} from "types/AssistantTypes";
import styles from "./rendimentiCumulatiCompare.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";

type RendimentiCumulatiCompareProps = {
  data: ProductCardMisureDiRischioType[];
  allProducts: ProductCardProductsType;
  isinToName: ProductCardIsinToNameType;
  isAllBondProducts: boolean;
};

const RendimentiCumulatiCompareComponent: React.FC<
  RendimentiCumulatiCompareProps
> = ({ data, allProducts, isinToName, isAllBondProducts }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [columns, setColumns] = useState(3);
  const rows = Array.from({ length: 10 }, (_, rowIndex) => rowIndex + 1);

  useEffect(() => {
    //** set number of columns based on the number of product we want to compare */
    if (allProducts) {
      const productCount = Object.keys(allProducts).length;
      setColumns(productCount + 1);
    }
  }, [allProducts]);

  // Memoize cells calculation based on rows and columns
  const cells = useMemo(
    () =>
      rows.flatMap((row) =>
        Array.from({ length: columns }, (_, colIndex) => ({
          row,
          col: colIndex + 1,
        })),
      ),
    [rows, columns],
  );

  const dashValue = (dataValue: any): any => {
    if (!dataValue || dataValue === undefined) {
      return "-";
    }
    return dataValue;
  };

  // Memoize cell rendering logic
  const renderedCells = useMemo(() => {
    return cells.map((cell) => {
      const { row, col } = cell;

      if (row === 1) {
        if (col === 1) {
          return (
            <div key={`${row}-${col}`} className={[styles.titleCell].join(" ")}>
              {t("productCard.rendCumulati")}
            </div>
          );
        }
        const isin = allProducts[col - 2];
        const product = isinToName[isin];
        return (
          <div
            key={`${row}-${col}`}
            className={[styles.cell, styles.productName].join(" ")}
          >
            {product}
          </div>
        );
      }
      if (row > 1) {
        if (col === 1 && row === 2) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellKeys,
                styles.firstCellKey,
                styles.cellWithBorder,
              ].join(" ")}
            >
              <div style={{ paddingBottom: 7, backgroundColor: "" }}>
                {data[row - 2].label}
              </div>
            </div>
          );
        }
        if (col === 1 && row > 2) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellKeys,
                row < 10 ? styles.cellWithBorder : styles.cellWithNoBorder,
              ].join(" ")}
            >
              {data[row - 2].label}
            </div>
          );
        }

        if (col > 1 && row === 2) {
          return (
            <div key={`${row}-${col}`}>
              <div className={styles.cellValueInnerHeader}>
                {/* {data[row - 2].category_values[col - 2]} */}
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <div className={styles.pad848}>Fondo %</div>
                    <div
                      className={[
                        styles.cellValueInnerHeaderValue,
                        styles.cellWithBorder,
                      ].join(" ")}
                      style={{
                        lineHeight: "40px",
                        color:
                          parseFloat(data[row - 2].category_values[col - 2]) < 0
                            ? colorsConfig.red
                            : "inherit",
                      }}
                    >
                      {dashValue(data[row - 2].category_values[col - 2])}
                    </div>
                  </div>

                  {!isAllBondProducts && (
                    <div
                      style={{
                        width: "100%",
                      }}
                    >
                      <div className={styles.pad848}>Categoria %</div>
                      <div
                        className={[
                          styles.cellValueInnerHeaderValue,
                          styles.cellWithBorder,
                        ].join(" ")}
                        style={{
                          textAlign: "center",
                          lineHeight: "40px",
                          color:
                            parseFloat(data[row - 2].product_values[col - 2]) <
                            0
                              ? colorsConfig.red
                              : "inherit",
                        }}
                      >
                        {dashValue(data[row - 2].product_values[col - 2])}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
        if (
          col > 1 &&
          row - 2 < data.length &&
          row !== 2 &&
          col - 2 < data[row - 2].category_values.length
        ) {
          return (
            <div key={`${row}-${col}`}>
              <div
                className={[
                  styles.cellValue,
                  styles.cellValueInner,
                  row < 10 ? styles.cellWithBorder : styles.cellWithNoBorder,
                ].join(" ")}
              >
                {/* {data[row - 2].category_values[col - 2]} */}
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    className={styles.pad8}
                    style={{
                      color:
                        parseFloat(data[row - 2].product_values[col - 2]) < 0
                          ? colorsConfig.red
                          : "inherit",
                    }}
                  >
                    {dashValue(data[row - 2].product_values[col - 2])}
                  </div>
                  {!isAllBondProducts && (
                    <div
                      className={styles.pad8}
                      style={{
                        color:
                          parseFloat(data[row - 2].category_values[col - 2]) < 0
                            ? colorsConfig.red
                            : "inherit",
                      }}
                    >
                      {dashValue(data[row - 2].category_values[col - 2])}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
      }

      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          -
        </div>
      );
    });
  }, [cells, allProducts, isinToName, data]);

  return (
    <div className={styles.container}>
      <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
        {renderedCells}
      </div>
    </div>
  );
};

// Use React.memo to prevent re-renders if props haven't changed
export const RendimentiCumulatiCompare = memo(
  RendimentiCumulatiCompareComponent,
  (prevProps, nextProps) => {
    // Only re-render if data changes
    return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);
  },
);

export default RendimentiCumulatiCompare;
